import React from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate, useLocation } from 'react-router-dom';

/**
 * Gates routes by auth status and (optionally) by role.
 *  • `allow` lists which roles may view the route.
 *  • Other roles are redirected to their natural home (/admin, /partner, /dashboard).
 *  • A bare <ProtectedRoute> still allows any authed user, except partners are
 *    forced onto /partner so they never accidentally see the regular user dashboard.
 *  • Unauthenticated users keep their intended URL (passed via location state)
 *    so deep-links from emails land users back on the right page after login.
 */
export const ProtectedRoute = ({ children, allow }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  const role = user?.role;
  const home = role === 'admin' ? '/admin' : role === 'partner' ? '/partner' : '/dashboard';

  if (allow && !allow.includes(role)) {
    return <Navigate to={home} replace />;
  }

  // No explicit allow-list: privileged roles get bounced to their own dashboard
  // so the user-facing pages (Dashboard, Assets, AI Insights, Marketplace, etc.)
  // never accidentally surface to admins or partners.
  if (!allow && (role === 'partner' || role === 'admin')) {
    return <Navigate to={home} replace />;
  }

  return children;
};
